import React from 'react';
import { AlertCircle, RefreshCw, Home, ArrowLeft, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../config/api';
import { getSupabaseClient } from '../supabaseClient';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      componentStack: null,
      showDetails: false, // Toggle error details
      reportSent: false // Track if error was reported
    };
  }

  static getDerivedStateFromError(error) {
    return { 
      hasError: true, 
      error 
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    console.error('Error caught by boundary:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Update state with component stack
    this.setState({
      errorInfo: errorInfo,
      componentStack: errorInfo.componentStack
    });

    // Send error report to backend
    this.sendErrorReport(error, errorInfo);
  }

  logErrorToDatabase = async (error, userInfo) => {
    try {
      // Get the stored business type if available, otherwise default to a basic type
      const storedBusinessType = localStorage.getItem('businessType') || 'laundromat';
      const supabase = getSupabaseClient(storedBusinessType);

      // Get current user if available
      const { data: { user } } = await supabase.auth.getUser();
      const accountId = user?.id || null;

      // 1. Add row to logs_errors table
      const { data: errorLog, error: errorLogError } = await supabase
        .from('logs_errors')
        .insert([{
          account_id: accountId,
          error_type: 'ui'
        }])
        .select()
        .single();

      if (errorLogError) {
        console.error('Failed to log error:', errorLogError);
        return;
      }

      // 2. Add row to logs_errors_ui table
      await supabase
        .from('logs_errors_ui')
        .insert([{
          error_log_id: errorLog.id,
          page: window.location.href,
          screen_size: userInfo.screenSize,
          user_agent: userInfo.userAgent,
          error_message: error.message,
          error_stack: error.stack,
          timestamp: userInfo.timestamp,
          browser_language: userInfo.language,
          app_version: process.env.REACT_APP_VERSION || import.meta.env.VITE_APP_VERSION || 'unknown'
        }]);

      return errorLog.id;
    } catch (dbError) {
      console.error('Error logging to database:', dbError);
      return null;
    }
  };

  sendErrorReport = async (error, errorInfo) => {
    try {
      // Basic user info that doesn't require authentication
      const userInfo = {
        userAgent: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString(),
        language: navigator.language,
      };

      // 1 & 2: Log error to database
      const errorLogId = await this.logErrorToDatabase(error, userInfo);

      // 3: Create the error payload for the API
      const errorPayload = {
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name,
          errorLogId // Include the database log ID if available
        },
        userInfo,
        location: window.location.href,
      };

      // Send to the API endpoint
      const response = await fetch(`${API_BASE_URL}/api/email/error/ui`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorPayload),
      });

      if (response.ok) {
        this.setState({ reportSent: true });
        console.log('Error report sent successfully');
      } else {
        console.error('Failed to send error report:', await response.text());
      }
    } catch (reportError) {
      console.error('Error while reporting error:', reportError);
    }
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  }

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      // Animation variants
      const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { 
          opacity: 1,
          transition: { duration: 0.3 }
        }
      };

      const containerVariants = {
        hidden: { scale: 0.95, opacity: 0, y: 20 },
        visible: { 
          scale: 1, 
          opacity: 1, 
          y: 0,
          transition: { 
            type: "spring", 
            damping: 25, 
            stiffness: 300,
            duration: 0.4 
          }
        }
      };

      const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: (i) => ({ 
          opacity: 1, 
          y: 0,
          transition: { 
            delay: i * 0.1,
            duration: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }
        })
      };

      const buttonVariants = {
        hover: { scale: 1.03, transition: { duration: 0.2 } },
        tap: { scale: 0.97, transition: { duration: 0.1 } }
      };

      const detailsVariants = {
        closed: { 
          height: 0, 
          opacity: 0,
          transition: {
            height: { duration: 0.3 },
            opacity: { duration: 0.2 }
          }
        },
        open: { 
          height: 'auto', 
          opacity: 1,
          transition: {
            height: { duration: 0.3 },
            opacity: { duration: 0.2, delay: 0.1 }
          }
        }
      };

      return (
        <motion.div 
          className="min-h-screen flex items-center justify-center bg-black/[0.02] backdrop-blur-[80px] overflow-auto p-4 sm:p-6"
          initial="hidden"
          animate="visible"
          variants={backdropVariants}
        >
          <motion.div 
            className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-100 max-w-2xl w-full"
            variants={containerVariants}
          >
            <motion.div 
              className="flex items-center gap-4 mb-6"
              variants={itemVariants}
              custom={0}
              initial="hidden"
              animate="visible"
            >
              <div className="p-3 bg-red-50 rounded-full">
                <ShieldAlert className="w-6 h-6 sm:w-7 sm:h-7 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Something went wrong</h1>
                <p className="text-sm text-gray-500 mt-1">
                  We encountered an unexpected error
                </p>
              </div>
            </motion.div>

            {/* Error Message */}
            <motion.div 
              className="mb-6"
              variants={itemVariants}
              custom={1}
              initial="hidden"
              animate="visible"
            >
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 sm:p-5">
                <p className="text-red-700 font-medium text-sm sm:text-base">
                  {this.state.error?.message || 'An unexpected error occurred'}
                </p>
                {this.state.reportSent && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> 
                    Error has been reported to our team
                  </p>
                )}
              </div>
            </motion.div>

            {/* Toggle Details Button */}
            {isDevelopment && this.state.componentStack && (
              <motion.div
                variants={itemVariants}
                custom={2}
                initial="hidden"
                animate="visible"
                className="mb-4"
              >
                <motion.button
                  onClick={this.toggleDetails}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1.5"
                >
                  {this.state.showDetails ? 'Hide technical details' : 'Show technical details'}
                  <motion.span
                    animate={{ rotate: this.state.showDetails ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ArrowLeft className={`w-3.5 h-3.5 ${this.state.showDetails ? 'rotate-90' : ''}`} />
                  </motion.span>
                </motion.button>
              </motion.div>
            )}

            {/* Component Stack - Only in development */}
            <AnimatePresence>
              {isDevelopment && this.state.componentStack && this.state.showDetails && (
                <motion.div
                  variants={detailsVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className="mb-6 overflow-hidden"
                >
                  <h2 className="text-sm font-semibold text-gray-700 mb-2">Component Stack</h2>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 overflow-auto max-h-64">
                    <pre className="text-xs sm:text-sm font-mono text-gray-600 whitespace-pre-wrap">
                      {this.state.componentStack}
                    </pre>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <motion.div 
              className="flex flex-wrap gap-3 sm:gap-4"
              variants={itemVariants}
              custom={3}
              initial="hidden"
              animate="visible"
            >
              <motion.button
                onClick={() => window.location.reload()}
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
                className="flex items-center gap-2 px-4 py-2.5 sm:py-3 bg-black text-white rounded-xl transition-colors text-xs sm:text-sm font-medium"
              >
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Refresh Page
              </motion.button>

              <motion.button
                onClick={() => window.location.href = '/'}
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
                className="flex items-center gap-2 px-4 py-2.5 sm:py-3 border border-gray-200 bg-white text-gray-700 hover:text-gray-900 rounded-xl transition-colors text-xs sm:text-sm font-medium"
              >
                <Home className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Go to Home
              </motion.button>
            </motion.div>

            {/* Help text */}
            <motion.p 
              className="mt-6 text-xs sm:text-sm text-gray-500"
              variants={itemVariants}
              custom={4}
              initial="hidden"
              animate="visible"
            >
              If this error persists, try clearing your browser cache or contacting support.
            </motion.p>
          </motion.div>
        </motion.div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;